import {
  MODEL_REGISTRY,
  type ModelRecord,
} from '../data/modelRegistry'
import type {
  ModelTier,
  ProviderRecommendation,
} from '../domain/types'

export const PINNED_MODEL_STORAGE_KEY =
  'samrouter:pinned-model-v1'
export const PINNED_PROVIDER_STORAGE_KEY =
  'samrouter:pinned-provider-v1'

export type PinnedPreference = {
  pinnedModel: ModelRecord | null
  pinnedProvider: string | null
}

const TIER_RANK: Record<ModelTier, number> = {
  direct: 0,
  standard: 1,
  deep: 2,
}

export function findModel(
  modelId: string | null,
  provider?: string,
): ModelRecord | null {
  if (!modelId) return null
  return (
    MODEL_REGISTRY.find(
      (model) =>
        model.modelId === modelId &&
        (provider === undefined || model.provider === provider),
    ) ?? null
  )
}

export function readPinnedModel(): ModelRecord | null {
  if (typeof window === 'undefined') return null

  try {
    const storedReference = window.localStorage.getItem(
      PINNED_MODEL_STORAGE_KEY,
    )
    if (!storedReference) return null

    const parsed = JSON.parse(storedReference) as {
      provider?: unknown
      modelId?: unknown
    }
    if (
      typeof parsed.provider !== 'string' ||
      typeof parsed.modelId !== 'string'
    ) {
      return null
    }

    return findModel(parsed.modelId, parsed.provider)
  } catch {
    return null
  }
}

export function findProvider(provider: string | null): string | null {
  if (!provider) return null
  return MODEL_REGISTRY.some((model) => model.provider === provider)
    ? provider
    : null
}

export function readPinnedProvider(): string | null {
  if (typeof window === 'undefined') return null

  try {
    const storedReference = window.localStorage.getItem(
      PINNED_PROVIDER_STORAGE_KEY,
    )
    if (!storedReference) return null

    const parsed = JSON.parse(storedReference) as {
      provider?: unknown
    }
    if (typeof parsed.provider !== 'string') return null

    return findProvider(parsed.provider)
  } catch {
    return null
  }
}

export function readPinnedPreference(): PinnedPreference {
  const pinnedModel = readPinnedModel()
  return {
    pinnedModel,
    pinnedProvider: pinnedModel ? null : readPinnedProvider(),
  }
}

export function writePinnedModel(model: ModelRecord | null): boolean {
  try {
    if (model) {
      window.localStorage.removeItem(PINNED_PROVIDER_STORAGE_KEY)
      window.localStorage.setItem(
        PINNED_MODEL_STORAGE_KEY,
        JSON.stringify({
          provider: model.provider,
          modelId: model.modelId,
        }),
      )
    } else {
      window.localStorage.removeItem(PINNED_MODEL_STORAGE_KEY)
      window.localStorage.removeItem(PINNED_PROVIDER_STORAGE_KEY)
    }
    return true
  } catch {
    return false
  }
}

export function writePinnedProvider(provider: string | null): boolean {
  try {
    if (provider) {
      window.localStorage.removeItem(PINNED_MODEL_STORAGE_KEY)
      window.localStorage.setItem(
        PINNED_PROVIDER_STORAGE_KEY,
        JSON.stringify({ provider }),
      )
    } else {
      window.localStorage.removeItem(PINNED_PROVIDER_STORAGE_KEY)
      window.localStorage.removeItem(PINNED_MODEL_STORAGE_KEY)
    }
    return true
  } catch {
    return false
  }
}

export function modelForProvider(
  provider: string | null,
  routeTier: ModelTier,
): ModelRecord | null {
  if (!provider) return null
  const routeRank = TIER_RANK[routeTier]

  return (
    MODEL_REGISTRY.filter((model) => model.provider === provider).sort(
      (left, right) => {
        const leftRank = TIER_RANK[left.tier]
        const rightRank = TIER_RANK[right.tier]
        const leftDistance =
          leftRank >= routeRank
            ? leftRank - routeRank
            : 10 + routeRank - leftRank
        const rightDistance =
          rightRank >= routeRank
            ? rightRank - routeRank
            : 10 + routeRank - rightRank
        return leftDistance - rightDistance
      },
    )[0] ?? null
  )
}

export function modelsForRecommendations(
  recommendations: ProviderRecommendation[],
): ModelRecord[] {
  return recommendations
    .map((recommendation) =>
      findModel(recommendation.modelId, recommendation.provider),
    )
    .filter((model): model is ModelRecord => model !== null)
}

export function uniqueModels(models: ModelRecord[]): ModelRecord[] {
  const seen = new Set<string>()
  return models.filter((model) => {
    const key = `${model.provider}:${model.modelId}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function sameModel(
  left: ModelRecord | null,
  right: ModelRecord,
): boolean {
  return (
    left?.provider === right.provider &&
    left.modelId === right.modelId
  )
}
